import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/Profile.css";

export default function Profile() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState({ type: "", message: "" });

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      nav("/login");
      return;
    }
    const fetchProfile = async () => {
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setForm(snap.data());
        } else {
          // Initialize user profile in Firestore
          const newProfile = {
            name: user.displayName || "",
            email: user.email || "",
            phone: "",
            createdAt: serverTimestamp(),
          };
          await setDoc(ref, newProfile);
          setForm(newProfile);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        setStatus({ type: "error", message: "Failed to load profile." });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user, nav]);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;

    // Basic validation
    if (!form.name.trim()) {
      setStatus({ type: "error", message: "Name cannot be empty." });
      return;
    }
    if (form.phone && !/^\d{10}$/.test(form.phone)) {
      setStatus({
        type: "error",
        message: "Phone number must be 10 digits.",
      });
      return;
    }

    setSaving(true);
    try {
      const ref = doc(db, "users", user.uid);
      await setDoc(
        ref,
        { ...form, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setStatus({ type: "success", message: "Profile updated successfully!" });
    } catch (err) {
      console.error("Save error:", err);
      setStatus({ type: "error", message: "Failed to update profile." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="muted">Loading profile...</p>;

  return (
    <section className="container page profile-page">
      <motion.div
        className="profile-card"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <h2 className="profile-title">My Profile</h2>

        {/* Status message */}
        <AnimatePresence>
          {status.message && (
            <motion.div
              key="status"
              className={`status-msg ${status.type}`}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {status.message}
            </motion.div>
          )}
        </AnimatePresence>

        <form className="form profile-form" onSubmit={handleSave}>
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Your name"
          />

          <label htmlFor="email">Email (cannot edit)</label>
          <input id="email" type="email" name="email" value={form.email} disabled />

          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="text"
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="Your 10-digit phone number"
          />

          <button className="btn" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </form>
      </motion.div>
    </section>
  );
}
